import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import FormField from '../ui/form-field';
import PriceField from '../ui/price-field';
import Switch from '../ui/switch';
import InputImage from '../ui/input-image';
import { Option } from '../../model/professional/option-model';
import optionFormViewModel from '../../view-model/professional/company/option-form-view-model';

interface OptionFormProps {
  mode: 'create' | 'edit';
  categoryId: string;
  option?: Option;
  onSuccess: (optionId: string | null) => void;
  onCancel: () => void;
}

/**
 * OptionForm - Composant pour la création et l'édition d'options
 * 
 * Ce composant suit le pattern MVVM en utilisant le OptionFormViewModel pour gérer
 * la logique métier et les interactions avec l'API.
 */
const OptionForm: React.FC<OptionFormProps> = observer(({ 
  mode, 
  categoryId, 
  option, 
  onSuccess, 
  onCancel 
}) => {

  useEffect(() => {
    optionFormViewModel.resetImageState(option?.image || null);
    optionFormViewModel.initializeForm(option || null);

    return () => {
      optionFormViewModel.resetForm();
    };
  }, [option?.id]);

  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      // Préparation des données du formulaire
      const formData = new FormData(e.currentTarget);
      
      formData.append('categoryId', categoryId);
      
      const isActive = formData.get('optionIsActive') === 'on';
      formData.delete('optionIsActive');
      formData.append('isActive', isActive ? 'true' : 'false');

      // Soumission du formulaire selon le mode
      let result: string | null = null;
      if (mode === 'create') {
        result = await optionFormViewModel.submitCreateForm(formData);
      } else if (option) {
        const currentIsActive = option.isActive !== undefined ? option.isActive : true;
        const success = await optionFormViewModel.submitUpdateForm(
          option.id, 
          formData, 
          currentIsActive, 
          isActive,
          option.image
        );
        result = success ? option.id : null;
      }

      onSuccess(result);
    } catch (error) {
      console.error('Error submitting option:', error);
    }
  };

  const isDisabled = optionFormViewModel.isLoading;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">
          {mode === 'create' ? t('services.addOption') : t('services.editOption')}
        </h3>
        <button 
          type="button"
          onClick={onCancel}
          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <X size={18} className="text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          autoFocus
          id="name"
          name="name"
          label={t('services.optionName')}
          type="text"
          defaultValue={option?.name || ''}
          placeholder={t('services.optionNamePlaceholder') || ''}
          required
        />
        
        <FormField
          id="description"
          name="description"
          label={t('services.optionDescription')}
          type="textarea"
          defaultValue={option?.description || ''}
          placeholder={t('services.optionDescriptionPlaceholder') || ''}
        />
        
        <PriceField
          id="price"
          name="price"
          label={t('services.optionPrice')}
          defaultValue={option?.price || 0}
          required
        />

        <FormField
          id="durationMinutes"
          name="durationMinutes"
          label={t('services.optionDuration', 'Durée additionnelle (minutes)')}
          type="number"
          defaultValue={option?.durationMinutes || 0}
          placeholder={t('services.optionDurationPlaceholder', 'Temps ajouté au service (0 = aucun)') || ''}
        />

        <InputImage
          id="image"
          name="image"
          label={t('services.optionImage')}
          imagePreview={optionFormViewModel.imagePreview}
          onImageChange={(file) => optionFormViewModel.handleImageChange(file)}
          onRemoveImage={() => optionFormViewModel.handleRemoveImage()}
        />
        
        <Switch
          id="optionIsActive"
          name="optionIsActive"
          label={t('services.optionActive')}
          defaultChecked={option?.isActive !== undefined ? option.isActive : true}
        />

        <div className="flex justify-end space-x-2 pt-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={isDisabled}
          >
            {t('services.cancel')}
          </Button>
          <Button 
            type="submit" 
            disabled={isDisabled}
          >
            {isDisabled ? t('services.saving') : mode === 'create' ? t('services.create') : t('services.update')}
          </Button>
        </div>
      </form>
    </div>
  );
});

export default OptionForm;
